// Shared utility functions for node components
// Extracted from legacy node files to support the universal node system

import type { Node, Edge } from '@xyflow/react'
import type { NodeInput } from '@/features/backtest/types/nodes'
import type { ParameterValue, ParametersRecord } from '@/features/backtest/schemas/parameter-value'
import { buildEdgeAdjacency } from '@/features/backtest/utils/connection-utils'
import type { EdgeAdjacency } from '@/features/backtest/utils/connection-utils'
import { isPassthroughType } from '@/features/backtest/utils/inline-passthroughs'
import { LEGACY_FACES, ohlcvSummary } from '@/features/backtest/schemas/node-labels'
import { normalizeCustomName } from '@/features/backtest/utils/node-display-name'
import { registerAllNodeSchemas } from '@/features/backtest/schemas/register-all'
import {
  UniversalNodeRegistry,
  type NodeFace,
} from '@/features/backtest/schemas/universal-node-schema'

/**
 * Normalize a node's `data.parameters` into a keyed record.
 *
 * The `ParametersRecord` type is a record, but the editor stores and persists
 * `data.parameters` as an ARRAY of `{ name, value }` (see saved graph JSON).
 * Indexing that array by key (`params['period']`) silently returns `undefined`,
 * which made indicator pills fall back to their schema default — e.g. a
 * connected EMA(89) rendered as "EMA(20)". Accept either shape and always
 * return a record so downstream `params['x']` reads work.
 */
function toParamsRecord(raw: unknown): ParametersRecord {
  if (Array.isArray(raw)) {
    const entries = raw
      .filter(
        (p): p is { name: string; value: ParameterValue } =>
          !!p && typeof p === 'object' && 'name' in p,
      )
      .map((p) => [p.name, p.value] as const)
    return Object.fromEntries(entries)
  }
  return (raw ?? {}) as ParametersRecord
}

/** A node's label rules: its schema's face, or a legacy type's (old saved strategies). */
function labelsFor(type: string | undefined): Partial<NodeFace> | undefined {
  if (!type) return undefined
  // Idempotent; callers outside the editor (form builder, Learn) need not have run it.
  registerAllNodeSchemas()
  return UniversalNodeRegistry.getSchema(type)?.face ?? LEGACY_FACES[type]
}

/**
 * Short display name for a node type ("EMA", "RSI", "OHLCV-1m") with a
 * fallback for types without one. Used wherever space is tight
 * (exposed-param aliases, pills).
 */
export function getNodeShortName(nodeType: string | undefined, fallback: string): string {
  return labelsFor(nodeType)?.shortName || fallback
}

// The only values allowed in an OHLCV field slot (see resolveConnectionLabel).
const OHLCV_FIELD_NAMES = new Set(['open', 'high', 'low', 'close', 'volume'])

/**
 * The node named with its settings ("EMA(20)", "BB(20,2)"), else its short
 * name, else its type in capitals. Exported: the form builder's operand labels
 * (form-builder/format-operand.ts) must render the exact same notation as the
 * canvas connection pills.
 */
export function getBaseDisplayName(type: string, params: ParametersRecord): string {
  const labels = labelsFor(type)
  return labels?.name?.(params) ?? labels?.shortName ?? (type?.toUpperCase() || 'Connected')
}

/**
 * Centralized function to resolve the best display label for a connection.
 * Recursively traces through the source's own inputs: each node's face says
 * how (`summary` in schemas/node-labels.ts), so "Close-5m > SMA(9)" reads its
 * inputs' labels, and a Bar Offset reads its upstream's.
 *
 * @param nodeId - The node that has the connection
 * @param inputId - The input handle ID on that node
 * @param allNodes - All nodes in the graph
 * @param allEdges - All edges in the graph
 * @param targetNode - Optional: the node being resolved (for recursion)
 * @param depth - Recursion depth limit to prevent infinite loops
 * @param adjacency - Optional pre-built adjacency map for O(1) lookups; built lazily otherwise
 */
export function resolveConnectionLabel(
  nodeId: string,
  inputId: string,
  allNodes: Node[],
  allEdges: Edge[],
  targetNode?: Node,
  depth: number = 0,
  adjacency?: EdgeAdjacency,
): string {
  // Prevent infinite recursion
  if (depth > 10) return 'Connected'

  const adj = adjacency ?? buildEdgeAdjacency(allNodes, allEdges)

  // Find the edge connecting to this input
  const edge = (adj.byTarget.get(nodeId) ?? []).find((e) => e.targetHandle === inputId)
  if (!edge) return 'No Input'

  // Find the source node — resolving group I/O pass-throughs to the REAL
  // upstream (the pill is plumbing; the label should read "Close-1m" or
  // "EMA", never "GROUPINPUT"). The original nodeId/inputId context is kept
  // so field-selection lookups below still hit the actual consumer.
  let sourceNode = adj.nodeById.get(edge.source)
  const seenPassthroughs = new Set<string>()
  while (
    sourceNode &&
    isPassthroughType((sourceNode.data?.type || sourceNode.type) as string) &&
    !seenPassthroughs.has(sourceNode.id)
  ) {
    seenPassthroughs.add(sourceNode.id)
    const upstreamEdge = (adj.byTarget.get(sourceNode.id) ?? [])[0]
    if (!upstreamEdge) return 'No Input'
    sourceNode = adj.nodeById.get(upstreamEdge.source)
  }
  if (!sourceNode) return 'Connected'
  const source = sourceNode

  // The receiving input's OHLCV field, which an OHLCV source folds into its
  // label so the pill is self-describing: picking "Close" in the field
  // dropdown makes the pill read `Close-1m`, and any downstream pill that
  // recurses back through priceData (via Bar Offset, etc.) sees the same
  // field. The source's *effective* output (after field selection) propagates
  // visibly downstream: if SMA reads `Close-1m`, the consumer of SMA shouldn't
  // have to guess which OHLC channel SMA was fed.
  //
  // Cookbook/AI-materialized graphs store the upstream STEP id (e.g. `price`)
  // in `source` with the real field in `field` — same contamination healed for
  // the engine payload in data-cleaning-utils. Only a real OHLCV field may
  // reach the label; anything else falls through to `field`, then `close`.
  const receivingNode = targetNode || adj.nodeById.get(nodeId)
  const inputData = (receivingNode?.data?.inputs as NodeInput[] | undefined)?.find(
    (inp: NodeInput) => inp?.name === inputId,
  )
  const consumerField =
    [inputData?.source, inputData?.field].find(
      (v): v is string => typeof v === 'string' && OHLCV_FIELD_NAMES.has(v),
    ) ?? 'close'

  return labelForSourceNode(source, consumerField, allNodes, allEdges, adj, depth)
}

/**
 * What a pill says about `source`, once the source is resolved (pass-throughs
 * already looked through): its custom name, else its summary with inputs
 * resolved the way pills resolve them, else its base display name.
 * `consumerField` is the OHLCV field the receiving input picks.
 */
export function labelForSourceNode(
  source: Node,
  consumerField: string,
  allNodes: Node[],
  allEdges: Edge[],
  adj: EdgeAdjacency,
  depth: number = 0,
): string {
  const sourceType = (source.data?.type || source.type) as string
  const sourceParams = toParamsRecord(source.data?.parameters)
  const summary = labelsFor(sourceType)?.summary

  // A renamed source is called by its user's name (utils/node-display-name),
  // and so is everything that reads it downstream; an OHLCV source still says
  // the field it hands this input ("Daily · Close").
  const customName = normalizeCustomName(source.data?.customName)
  if (customName) {
    return summary === ohlcvSummary
      ? `${customName} · ${consumerField[0].toUpperCase()}${consumerField.slice(1)}`
      : customName
  }
  if (!summary) return getBaseDisplayName(sourceType, sourceParams)

  const sourceInputs = source.data?.inputs as NodeInput[] | undefined
  return summary({
    params: sourceParams,
    input: (handle) =>
      resolveConnectionLabel(source.id, handle, allNodes, allEdges, source, depth + 1, adj),
    staticValue: (handle) => sourceInputs?.find((inp: NodeInput) => inp?.name === handle)?.value,
    wiredInputs: (adj.byTarget.get(source.id) ?? []).length,
    consumerField,
  })
}

/**
 * The name under a node's survey glyph (zoomed far out): what a downstream pill
 * would say about this node. Null when the node has nothing better than its
 * type name (subnets and group pass-throughs keep theirs).
 */
export function resolveSurveyLabel(
  node: Node,
  allNodes: Node[],
  allEdges: Edge[],
  adjacency?: EdgeAdjacency,
): string | null {
  const type = (node.data?.type || node.type) as string
  if (node.type === 'subnet' || isPassthroughType(type)) return null

  const customName = normalizeCustomName(node.data?.customName)
  if (customName) return customName

  const labels = labelsFor(type)
  const params = toParamsRecord(node.data?.parameters)
  // No single field applies to a whole OHLCV feed, so it reads "OHLCV-5m".
  if (labels?.summary === ohlcvSummary) {
    return `OHLCV-${(params['timeframe'] as string | undefined) || '1m'}`
  }

  const adj = adjacency ?? buildEdgeAdjacency(allNodes, allEdges)
  if (labels?.summary) return labelForSourceNode(node, 'close', allNodes, allEdges, adj)
  return labels?.name?.(params) ?? labels?.shortName ?? null
}

/**
 * Get connection pill display text based on connected source node type
 * This is the main entry point used by components - delegates to resolveConnectionLabel
 */
export function getConnectionPillDisplay(
  source: string | null,
  allNodes: Node[],
  allEdges: Edge[],
  nodeId: string,
  inputId: string,
  adjacency?: EdgeAdjacency,
): string {
  if (!source) return 'No Input'

  // Use the centralized resolver
  return resolveConnectionLabel(nodeId, inputId, allNodes, allEdges, undefined, 0, adjacency)
}

/**
 * Get dynamic text size based on content length
 */
export function getDynamicTextSize(text: string): string {
  if (!text) return 'text-xs'

  if (text.length <= 4) return 'text-xs'
  if (text.length <= 8) return 'text-xs'
  if (text.length <= 12) return 'text-xs'
  return 'text-xs' // Keep consistent small size
}

/**
 * Get field options for OHLCV data selection
 */
export function getFieldOptions() {
  return [
    { value: 'open', label: 'Open' },
    { value: 'high', label: 'High' },
    { value: 'low', label: 'Low' },
    { value: 'close', label: 'Close' },
    { value: 'volume', label: 'Volume' },
  ]
}

/**
 * Check if field selection should be shown for a node connection
 */
export function shouldShowFieldSelection(sourceNodeType: string): boolean {
  const ohlcvNodes = ['price-data', 'market-data']
  return ohlcvNodes.includes(sourceNodeType)
}
